import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ListagemGuiasRedePropria, ResumoGuiasRedePropria } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { CellStyle, ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  AgFilterUtils,
  LongColumnHintComponent,
  buildAutoGroupColumnDef,
  buildGridOptions,
  exportGridAsExcel,
} from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { ResumoGuiasRedePropriaActionCellRenderer } from './resumo-guias-rede-propria-action-cell.component';
import { ResumoGuiasRedePropriaService } from './resumo-guias-rede-propria.service';
import { FiliaisAtivas } from '@gestor-web/@core/api/ords/model/filiais-ativas';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const DIALOG_PADDING_MAX_OFF = 100;
const DIALOG_PADDING_MAX_ON = 35;

const GRID_KEY = 'ANALISES.RESUMO_GUIAS_REDE_PROPRIA';
const GRID_DIALOG_KEY = 'ANALISES.RESUMO_GUIAS_REDE_PROPRIA_DIALOG';
const GRID_KEY_RECEITA = 'ANALISES.RESUMO_GUIAS_REDE_PROPRIA_RECEITA';

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    hide: true,
  },
  {
    headerName: '014-CNPJ/CPF executante',
    field: 'cnpj_prestador',
    type: 'cnpj',
    width: 150,
  },
  {
    headerName: 'Nome do prestador',
    field: 'nome_prestador',
    sort: 'asc',
    width: 150,
  },
  {
    headerName: '022-Origem da guia (ID)',
    field: 'origem_guia',
    hide: true,
    cellClassRules: {
      'cell-red': params => {
        const ch = params.value?.toString()?.charAt(0);
        return !!ch && ch !== '3';
      },
    },
  },
  {
    headerName: '022-Origem da guia',
    field: 'nm_id_origem_guia',
    sort: 'asc',
    width: 170,
    cellClassRules: {
      'cell-red': params => {
        const ch = params.data?.origem_guia?.toString()?.charAt(0);
        return !!ch && ch !== '3';
      },
    },
  },
  {
    headerName: 'Tipo de rede',
    field: 'nm_tp_rede',
    sort: 'asc',
    width: 150,
  },
  {
    headerName: '021-Tipo de guia (ID)',
    field: 'tp_guia',
    sort: 'asc',
    hide: true,
  },
  {
    headerName: '021-Tipo de guia',
    field: 'nm_tp_guia',
    sort: 'asc',
    width: 150,
  },
  {
    headerName: '012-Cód. CNES executante',
    field: 'nr_cnes_exec',
    type: 'str',
    sort: 'asc',
    width: 200,
  },
  {
    headerName: 'Qtde. guias',
    field: 'qtde_guia',
    type: 'int',
    width: 120,
    cellRenderer: ResumoGuiasRedePropriaActionCellRenderer,
    cellClassRules: {
      'cell-yellow': params =>
        !params.node?.rowPinned &&
        !params.node?.footer &&
        (params.value == null || params.value === '' || +params.value === 0),
    },
  },
  {
    headerName: '050-Valor informado da guia',
    field: 'vl_informado',
    type: 'money',
    width: 170,
  },
  {
    headerName: '058-Valor total glosa',
    field: 'vl_glosa',
    type: 'money',
    width: 140,
    cellClassRules: {
      'cell-red': params => params.value != 0,
    },
  },
  {
    headerName: '059-Valor total pago',
    field: 'vl_pago',
    type: 'money',
    width: 130,
    cellClassRules: {
      'cell-red': params => params.value != 0,
    },
  },
  {
    headerName: 'Amostra Nº guia da operadora',
    field: 'amostra_nr_guia_operadora',
    width: 160,
    cellRenderer: LongColumnHintComponent,
  },
];

const COLUMNS_DIALOG: ColDef[] = [
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
  },
  {
    headerName: '014-CNPJ/CPF Executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nome do prestador',
    field: 'nome_prestador',
    width: 200,
  },
  {
    headerName: 'Tipo de rede (ID)',
    field: 'tp_rede',
    hide: true,
  },
  {
    headerName: 'Tipo de rede',
    field: 'nm_tp_rede',
  },
  {
    headerName: '012-CNES executante',
    field: 'nr_cnes_exec',
    type: 'str',
  },
  {
    headerName: '024-Nr. guia na operadora',
    field: 'nr_guia_operadora',
    type: 'str',
  },
  {
    headerName: '022-Origem da guia (ID)',
    field: 'id_origem_guia',
    hide: true,
  },
  {
    headerName: '022-Origem da guia',
    field: 'nm_id_origem_guia',
    cellClassRules: {
      'cell-red': params =>
        params.value == '1 Rede Contratada, referenciada ou credenciada' ||
        params.value == '2 Rede Própria - Cooperados',
    },
  },
  {
    headerName: '050-Valor informado da guia',
    field: 'vl_guia_informado',
    type: 'money',
  },
  {
    headerName: '058-Valor total glosa',
    field: 'vl_glosa',
    type: 'money',
    cellClassRules: {
      'cell-red': params => params.value != 0,
    },
  },
  {
    headerName: '059-Valor total pago',
    field: 'vl_pago',
    type: 'money',
    cellClassRules: {
      'cell-red': params => params.value != 0,
    },
  },
];

const COLUMNS_RECEITA: ColDef[] = [
  {
    headerName: 'CNPJ',
    field: 'nr_cnpj',
    type: 'cnpj',
    width: 160,
    minWidth: 140,
  },
  {
    headerName: 'Nome fantasia',
    field: 'nome_fantasia',
    sort: 'asc',
    width: 250,
    minWidth: 200,
  },
  {
    headerName: 'Nome empresarial',
    field: 'nm_empresarial',
    width: 250,
    minWidth: 200,
  },
  {
    headerName: 'Situação cadastral',
    field: 'situacao_cadastral',
    width: 150,
    minWidth: 130,
    valueFormatter: params => {
      if (params.value === 2 || params.value === '2') return 'ATIVA';
      if (params.value === 8 || params.value === '8') return 'BAIXADA';
      if (params.value === 3 || params.value === '3') return 'SUSPENSA';
      if (params.value === 4 || params.value === '4') return 'INAPTA';
      if (params.value === 1 || params.value === '1') return 'NULA';
      return params.value?.toString() || '';
    },
  },
  {
    headerName: 'CNAE principal',
    field: 'cnae',
    width: 220,
    minWidth: 120,
  },
  {
    headerName: 'CNAE secundário',
    field: 'ds_cnae_secundario',
    width: 300,
    minWidth: 200,
    cellRenderer: LongColumnHintComponent,
  },
  {
    headerName: 'Endereço',
    field: 'ds_endereco',
    minWidth: 350,
    flex: 2,
    cellRenderer: LongColumnHintComponent,
  },
];

/**
 * Classe da linha da aba "Filiais ativas na RFB" sem guias no ano-base.
 *
 * A filial é destacada quando nenhuma guia TISS da operadora no MON_ANO_BASE_VIGENTE tem o CNPJ dela
 * como executante (id_prestador = 0, calculado pelo worker_resumo_guias_rede_propria_filiais).
 */
export const FILIAL_SEM_GUIAS_ROW_CLASS = 'row-yellow';
export const FILIAL_SEM_GUIAS_TOOLTIP = 'Sem guias geradas para o estabelecimento neste ano-base';

/** Devolve a classe de destaque da filial, ou vazio quando ela tem guias no ano-base. */
export function filialSemGuiasRowClass(filial: FiliaisAtivas): string {
  return filial?.id_prestador === 0 ? FILIAL_SEM_GUIAS_ROW_CLASS : '';
}

/** Devolve o texto que explica o destaque da filial, ou vazio quando ela não é destacada. */
export function filialSemGuiasTooltip(filial: FiliaisAtivas): string {
  return filial?.id_prestador === 0 ? FILIAL_SEM_GUIAS_TOOLTIP : '';
}

// As colunas com LongColumnHintComponent já mostram o texto inteiro no title nativo; o tooltip do grid ficaria por cima.
export const COLUMNS_RECEITA_COM_TOOLTIP: ColDef[] = COLUMNS_RECEITA.map(coluna =>
  coluna.cellRenderer === LongColumnHintComponent
    ? coluna
    : { ...coluna, tooltipValueGetter: params => filialSemGuiasTooltip(params.data) },
);

@Component({
    templateUrl: './resumo-guias-rede-propria.component.html',
    styleUrls: ['./resumo-guias-rede-propria.component.scss'],
    providers: [ResumoGuiasRedePropriaService, DecimalPipe],
    standalone: false
})
export class ResumoGuiasRedePropriaComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly GRID_DIALOG_KEY = GRID_DIALOG_KEY;
  readonly GRID_KEY_RECEITA = GRID_KEY_RECEITA;

  @ViewChild('agGrid') agGrid: AgGridAngular<RowGuia>;
  @ViewChild('agGridDialog') agGridDialog: AgGridAngular<ListagemGuiasRedePropria>;
  @ViewChild('agGridReceita') agGridReceita: AgGridAngular<FiliaisAtivas>;

  gridOptions: GridOptions<RowGuia>;
  gridDialogOptions: GridOptions<ListagemGuiasRedePropria>;
  gridReceitaOptions: GridOptions<FiliaisAtivas>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;
  filterChangedFiliais = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowData: RowGuia[];

  activeTabIndex = 0;
  rowDataReceita: FiliaisAtivas[] = [];
  dtUltimoProcessamentoFiliais: string = null;
  private receitaLoadedForRegans: string | null = null;

  dialogDisplay = false;
  dialogDisplayFornecimento = false;
  dialogData: ListagemGuiasRedePropria[];
  dialogSubtitle = '';

  dialogHeightPadding = DIALOG_PADDING_MAX_OFF;

  selectedRow: RowGuia;
  selectedRowIndex: number;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  private ngDestroyed$ = new Subject<void>();

  constructor(
    private permissaoService: PermissaoService,
    private service: ResumoGuiasRedePropriaService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private datePipe: DatePipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        this.receitaLoadedForRegans = null;
        this.rowDataReceita = [];
        this.dtUltimoProcessamentoFiliais = null;

        this.filterChangedFiliais = true;

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        } else {
          this.filterChangedFiliais = true;
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  onTabChange(index: number) {
    this.activeTabIndex = index;
    if (index === 1 && this.rowDataReceita.length > 0) {
      setTimeout(() => this.reflowReceitaGrid(), 0);
    }
  }
  private reflowReceitaGrid() {
    const api = this.agGridReceita?.api;
    if (!api) return;
    api.sizeColumnsToFit?.();
    api.resetRowHeights?.();
    api.refreshCells?.({ force: true });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<RowGuia>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        // <RowGuia> explicito: sem ele o ag-grid 36 estoura a profundidade de inferencia (TS2589)
        autoGroupColumnDef: buildAutoGroupColumnDef<RowGuia>({
          cellStyle: params => {
            const groupedBy = params.colDef.showRowGroup as string;
            const firstCharFromValue = params.value?.toString()?.charAt(0);

            // Quando agrupando pelo ID
            if (groupedBy === 'origem_guia') {
              if (firstCharFromValue && firstCharFromValue !== '3') {
                const style: CellStyle = { color: '#ef4444' };
                return style;
              }
            }

            // Quando agrupando pelo nome
            if (groupedBy === 'nm_id_origem_guia') {
              if (firstCharFromValue && firstCharFromValue !== '3') {
                const style: CellStyle = { color: '#ef4444' };
                return style;
              }
            }

            return null;
          },
        }),
        excelStyles: [
          { id: 'cell-red', font: { color: '#ef4444' } },
          { id: 'cell-yellow', interior: { color: '#fff2aa', pattern: 'Solid' } },
        ],
      },
    );

    this.gridDialogOptions = buildGridOptions<ListagemGuiasRedePropria>(
      {
        columnDefs: COLUMNS_DIALOG,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: GRID_DIALOG_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: this.exportExcelDialog,
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );

    this.gridReceitaOptions = buildGridOptions<FiliaisAtivas>(
      {
        columnDefs: COLUMNS_RECEITA_COM_TOOLTIP,
        pipes: { cpfCnpj: this.cpfCnpjPipe },
        views: { gridKey: GRID_KEY_RECEITA, hide: !this.podeGerenciarVisoes },
        exportExcelFn: () => this.exportExcelReceita(),
      },
      {
        grandTotalRow: undefined,
        context: { parentComponent: this },
        getRowClass: params => filialSemGuiasRowClass(params.data),
        // Mesma cor da .row-yellow do .scss, para a exportação
        excelStyles: [{ id: FILIAL_SEM_GUIAS_ROW_CLASS, interior: { color: '#fff2aa', pattern: 'Solid' } }],
        tooltipShowDelay: 500,
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    if (this.activeTabIndex === 0) {
      this.filterChanged = false;
      this.loadResumoGuias();
    } else {
      this.filterChangedFiliais = false;
      this.loadReceita();
    }
  }

  private loadResumoGuias() {
    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]).subscribe({
      next: items => {
        if (!items?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        this.rowData = items.map(item => {
          const row: RowGuia = {
            ...item,
            nm_tp_rede: '',
            nm_tp_guia: '',
            nm_id_origem_guia: '',
          };

          row.nm_tp_rede = this.geralService.buildNomeDeDominio(row.tp_rede, 'MON_TIPO_REDE');
          row.nm_tp_guia = this.geralService.buildNomeDeDominio(row.tp_guia, 'MON_TIPO_GUIA');
          row.nm_id_origem_guia = this.geralService.buildNomeDeDominio(
            row.origem_guia?.charAt(0),
            'dm_origemEventoAtencaoSaude',
          );

          return row;
        });

        const totalData = items.reduce(
          (acc, item) => ({
            cnpj_prestador: null,
            nr_cnpj_cpf_exec: null,
            nome_prestador: null,
            origem_guia: null,
            tp_rede: null,
            nr_cnes_exec: null,
            amostra_nr_guia_operadora: null,
            qtde_guia: acc.qtde_guia + item.qtde_guia,
            vl_informado: acc.vl_informado + item.vl_informado,
            vl_glosa: acc.vl_glosa + item.vl_glosa,
            vl_pago: acc.vl_pago + item.vl_pago,
          }),
          {
            qtde_guia: 0,
            vl_informado: 0,
            vl_glosa: 0,
            vl_pago: 0,
          },
        );

        this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  loadReceita() {
    if (!this.operadora) return;
    if (!this.agGridReceita?.api) return;

    this.agGridReceita.api.setGridOption('loading', true);

    const ultimo$ = this.service.getFiliaisAtivasUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({ filiais: this.service.getFiliaisAtivas(this.operadora), ultimo: ultimo$ }).subscribe({
      next: ({ filiais, ultimo }) => {
        this.rowDataReceita = filiais ?? [];
        this.dtUltimoProcessamentoFiliais = ultimo ? this.datePipe.transform(ultimo, 'dd/MM/yyyy') : null;
        this.receitaLoadedForRegans = this.operadora;

        setTimeout(() => {
          this.reflowReceitaGrid();
          this.agGridReceita.api.setGridOption('loading', false);
        }, 0);
      },
      error: error => {
        this.notificationService.handleError(error);
        this.agGridReceita.api.setGridOption('loading', false);
      },
    });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
    this.filterChangedFiliais = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  showDialogFor(rowData: RowGuia, rowIndex: number) {
    this.selectedRow = rowData;
    this.showDialog(rowData as RowGuia);
  }

  private showDialog(rowData: RowGuia) {
    if (!this.dialogDisplay) {
      setTimeout(() => this.gridService.applyVisaoInicial(GRID_DIALOG_KEY, this.agGridDialog));
    }
    this.dialogDisplay = true;
    this.dialogDisplayFornecimento = false;

    this.agGridDialog?.api?.setGridOption('loading', true);

    const cleanCnpj = rowData.cnpj_prestador?.replace(/[.\-/\s]/g, '');

    const selectedCompetencia = (rowData as any)?.nr_competencia?.toString();
    const compIni = selectedCompetencia ?? this.competenciaRange[0];
    const compFim = selectedCompetencia ?? this.competenciaRange[1];

    const safeTpRede =
      rowData.tp_rede ?? ((rowData as any)?.nm_tp_rede?.toString()?.trim()?.charAt(0) as 'D' | 'I' | undefined);

    // Extract just the number from origem_guia string and convert to string
    const cleanOrigemGuia = rowData.origem_guia ? rowData.origem_guia.split(' ')[0] : undefined;

    this.service
      .getListagemGuiasRedePropria(
        this.operadora,
        compIni,
        compFim,
        cleanCnpj,
        cleanOrigemGuia,
        safeTpRede,
        rowData.nr_cnes_exec,
        rowData.tp_guia,
      )
      .subscribe({
        next: items => {
          this.dialogData = items.map(item => ({
            ...item,
            nm_tp_rede: this.geralService.buildNomeDeDominio(item.tp_rede, 'MON_TIPO_REDE'),
            nm_id_origem_guia: this.geralService.buildNomeDeDominio(item.id_origem_guia, 'MON_ORIGEM_GUIA'),
          }));
        },
        error: error => {
          this.notificationService.handleError(error);
        },
        complete: () => this.agGridDialog?.api?.setGridOption('loading', false),
      });
  }

  onDialogMaximize(event) {
    this.dialogHeightPadding = event.maximized ? DIALOG_PADDING_MAX_ON : DIALOG_PADDING_MAX_OFF;
  }

  onDialogHide(event) {
    // console.log('[ListagemGuiasRedePropria] onDialogHide', event);
    // this.router.navigate([this.routeCritica ? '..' : '.'], { relativeTo: this.route });
  }

 
  exportExcel(): void {
    const fileNamePrefix = `Resumo-Guias-Rede-Propria-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Prestadores com guias',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  exportExcelReceita(): void {
    const fileNamePrefix = `Filiais-Ativas-RFB-S${this.operadoraNome}-${this.operadora}`;
    const headers = [
      'Filiais ativas na RFB',
      `${this.operadoraNome} - ${this.operadora}`,
      'Exportado pelo Gestor IDSS em: ' + new Date().toLocaleString('pt-BR'),
      'Em amarelo: ' + FILIAL_SEM_GUIAS_TOOLTIP,
    ];
    exportGridAsExcel({ api: this.agGridReceita.api, headers, fileNamePrefix });
  }

  exportExcelDialog(): void {
    const headers = [
      'Listagem de guias de rede própria',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Exportado pelo Gestor IDSS em: ' + new Date().toLocaleString('pt-BR'),
    ];

    const currentGrid = this.agGridDialog;

    currentGrid.api.exportDataAsExcel({
      prependContent: AgFilterUtils.buildFilterRows(currentGrid.api, headers),
      fileName: `resumo-guias-rede-propria-${this.operadoraNome}-${this.competenciaRange[0]}-${
        this.competenciaRange[1]
      }-em-${new Date().toISOString().split('.', 1)[0].replace(/[-:T]/g, '')}.xlsx`,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface RowGuia extends ResumoGuiasRedePropria {
  nm_tp_rede: string;
  nm_tp_guia: string;
  nm_id_origem_guia: string;
}
